import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRow } from '@mui/x-data-grid-pro';

import AppLink from '@/components/AppLink';
import DataGrid from '@/components/DataGrid';
import AlertCircleIcon from '@/components/Icons/AlertCircleIcon';
import DotIcon from '@/components/Icons/Animated/DotIcon';
import { BasetenPageEnum } from '@/routes/types';
import { WorkspaceOrgSharedEndpointsFragment } from '@/sections/Overview/ModelAPIs/__generated__/fragments.generated';
import { usePinnedAPI } from '@/sections/Overview/ModelAPIs/hooks';
import { SharedEndpointIconSize } from '@/sections/SharedEndpoints/Icons/types';
import { mapModelFamilyToIcon } from '@/sections/SharedEndpoints/utils';
import { dateDisplay } from '@/utils/date';
import moment from 'moment';

import { dataGridProps, dataGridStyles } from '../../styled';
import Actions from '../Actions';
import { ListProps } from '../types';
import InferenceVolume from './InferenceVolume';

const ROW_HEIGHT = 67;

function Table({ orgSharedEndpoints, isSpendExceeded }: ListProps) {
  const { getPinActionItem } = usePinnedAPI();
  const columns: GridColDef<WorkspaceOrgSharedEndpointsFragment>[] = [
    {
      field: 'name',
      headerName: 'Name',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: ({ row }) => {
        const Icon = mapModelFamilyToIcon(row.sharedEndpoint.modelFamily);

        return (
          <Stack width="100%" gap={1} direction="row" alignItems="center">
            <Icon size={SharedEndpointIconSize.SMALL} />
            <Typography noWrap variant="h5">
              {row.sharedEndpoint.name}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={isSpendExceeded ? 'Budget reached' : 'Active'}
          color={isSpendExceeded ? 'error' : 'success'}
          icon={isSpendExceeded ? <AlertCircleIcon /> : <DotIcon />}
        />
      ),
    },
    {
      field: 'requests',
      headerName: 'Requests',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }) => <InferenceVolume id={row.id} />,
    },
    {
      field: 'created',
      headerName: 'Added',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }) => {
        const displayTime = dateDisplay(moment(row.created));

        return (
          <Tooltip arrow title={displayTime}>
            <Typography variant="body1">{displayTime}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 110,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" gap={0.2}>
            {getPinActionItem(row.id, row.sharedEndpoint.name)}
            <Actions
              variant="minimal"
              sharedEndpointId={row.id}
              sharedEndpointName={row.sharedEndpoint.name}
            />
          </Stack>
        );
      },
    },
  ];

  return (
    <Box data-cy="model-api-table-content">
      <DataGrid<WorkspaceOrgSharedEndpointsFragment>
        rows={orgSharedEndpoints}
        columns={columns}
        rowHeight={ROW_HEIGHT}
        getRowId={(row) => row.id}
        sx={dataGridStyles}
        {...dataGridProps}
        slots={{
          row: (props) => (
            <AppLink
              page={BasetenPageEnum.SharedEndpoint}
              params={{ sharedEndpointId: props.row.id }}
              key={props.row}
              underline="none"
              sx={{ color: 'inherit', '&:hover': { textDecoration: 'none' } }}
            >
              <GridRow {...props} />
            </AppLink>
          ),
        }}
      />
    </Box>
  );
}

export default Table;
